/** CRO buy box. All IDs are explicit: plan order/name never determines billing. */
(() => {
  if (customElements.get('sova-buybox')) return;
  const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const sameId = (a, b) => /^\d+$/.test(String(a)) && String(a) === String(b);
  const hasPlan = (variant, planId) => variant.plans.some(p => sameId(p.id, planId));
  const INTERFACES = ['control', 'A', 'B1', 'B2'];
  // #buybox-b1 picks an interface on the normal page, like a variant URL:
  // real offer only, counted as regular traffic, switches live when changed.
  const hashVariant = () => {
    const value = location.hash.match(/^#buybox-(control|a|b1|b2)$/i)?.[1];
    if (!value) return null;
    return value.toLowerCase() === 'control' ? 'control' : value.toUpperCase();
  };
  // ?sova_buybox=B1 is the review mode: flagged qa. Intelligems sends real traffic
  // here, so without usable plans it keeps the regular buy block like everyone else.
  const qaVariant = () => {
    const value = new URLSearchParams(location.search).get('sova_buybox');
    return INTERFACES.includes(value) ? value : null;
  };

  class SovaBuybox extends HTMLElement {
    connectedCallback() {
      if (!this.watchingHash) {
        this.watchingHash = true;
        window.addEventListener('hashchange', () => this.onHash());
      }
      if (this.abort) return;
      try {
        this.config = JSON.parse(this.querySelector('[data-buybox-config]').textContent);
        const qa = qaVariant();
        const hash = hashVariant();
        this.host = this.closest('.product-form');
        if (!this.host || (this.config.country !== 'FR' && !this.config.preview) || !this.validate()) return;
        this.money = new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency });
        this.abort = new AbortController();
        this.state = { duration: 3, mode: 'subscription', billing: this.defaultBilling() };
        this.dialogStep = 0;
        this.addEventListener('click', e => this.click(e), { signal: this.abort.signal });
        this.addEventListener('change', e => this.change(e), { signal: this.abort.signal });
        window.addEventListener('sova:buybox:assign', e => {
          if (!e.detail?.handle || e.detail.handle === this.config.handle) this.activate(e.detail?.variant);
        }, { signal: this.abort.signal });
        window.addEventListener('pagehide', () => {
          if (this.dialog?.open) this.track('abandon', { step: this.stepName() });
        }, { signal: this.abort.signal });
        this.qa = !!qa;
        this.activate(qa || hash || window.SovaBuybox?.variant || this.config.variant);
      } catch (error) {
        this.hidden = true;
        this.host?.removeAttribute('data-cro-active');
        console.warn('[Sova buy box] Control retained:', error.message);
      }
    }

    onHash() {
      const variant = hashVariant() || (location.hash ? null : this.config?.variant);
      if (!variant || !this.isConnected) return;
      if (this.abort) this.activate(variant);
      else this.connectedCallback();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.spacingObserver?.disconnect();
      this.abort?.abort();
      this.abort = null;
      this.closeDialog();
      this.host?.removeAttribute('data-cro-active');
    }

    validate() {
      this.demo = false;
      if (this.validatePlans()) return true;
      return this.config.preview ? this.simulate() : false;
    }

    // Theme editor only (request.design_mode): lets the team review the layout
    // before Loop plans exist. Prices are simulated and nothing can reach the cart.
    simulate() {
      const c = this.config;
      const detected = this.detect() || {};
      const one = detected[1] || c.variants[0];
      const three = detected[3] || c.variants.find(v => v !== one);
      if (!one || !three || one === three || ![one, three].every(v => Number.isInteger(v.price) && v.price > 0)) return false;
      const plan = charge => ({ id: null, recurring: true, price: charge, charge, remaining: 0 });
      const quarterly = Math.round(three.price * 0.85);
      this.variants = { 1: one, 3: three };
      this.plans = { month: plan(Math.round(one.price * 0.9)), monthly: plan(Math.round(quarterly / 3)), quarterly: plan(quarterly) };
      this.demo = true;
      return true;
    }

    // Plans are read from their price structure, never from names or positions.
    // Optional theme-setting IDs pin them explicitly. Otherwise the 3-month
    // variant is the one carrying two recurring plans where one charges exactly
    // a third of the other (monthly billing vs quarterly), and the 1-month
    // variant is the only other one carrying a single recurring plan. Any
    // ambiguity keeps the regular buy block.
    detect() {
      const c = this.config;
      const recurring = v => v.plans.filter(p => p.recurring);
      if (c.monthPlanId || c.quarterMonthlyPlanId || c.quarterPlanId) {
        const find = id => v => v.plans.find(p => sameId(p.id, id));
        const one = c.variants.find(v => hasPlan(v, c.monthPlanId));
        const three = c.variants.find(v => hasPlan(v, c.quarterPlanId) && (!c.quarterMonthlyPlanId || hasPlan(v, c.quarterMonthlyPlanId)));
        if (sameId(c.quarterMonthlyPlanId, c.quarterPlanId)) return null;
        return one && three ? { 1: one, 3: three, plans: { month: find(c.monthPlanId)(one), monthly: c.quarterMonthlyPlanId ? find(c.quarterMonthlyPlanId)(three) : null, quarterly: find(c.quarterPlanId)(three) } } : null;
      }
      const pairOf = v => {
        const plans = recurring(v);
        for (const monthly of plans) {
          for (const quarterly of plans) {
            if (monthly !== quarterly && Math.abs(monthly.charge * 3 - quarterly.charge) <= 2) return { monthly, quarterly };
          }
        }
        return null;
      };
      const threes = c.variants.filter(pairOf);
      if (threes.length > 1) return null;
      if (threes.length === 1) {
        const three = threes[0];
        const ones = c.variants.filter(v => v !== three && recurring(v).length === 1);
        if (ones.length !== 1) return null;
        return { 1: ones[0], 3: three, plans: { month: recurring(ones[0])[0], ...pairOf(three) } };
      }
      // Without a monthly-billed 3-month plan: exactly two variants with one
      // recurring plan each. The cheaper one is the 1-month variant; the 3-month
      // subscription is then paid quarterly and no payment choice is offered.
      const single = c.variants.filter(v => recurring(v).length === 1);
      if (single.length !== 2 || c.variants.some(v => recurring(v).length > 1)) return null;
      const [one, three] = [...single].sort((a, b) => a.price - b.price);
      if (one.price === three.price) return null;
      return { 1: one, 3: three, plans: { month: recurring(one)[0], monthly: null, quarterly: recurring(three)[0] } };
    }

    validatePlans() {
      const found = this.detect();
      if (!found) return false;
      const one = found[1], three = found[3];
      if (one.id === three.id || one.requiresSellingPlan || three.requiresSellingPlan) return false;
      // Deferred/irregular charges need a separate contract integration. Never fake installments.
      const { month, monthly, quarterly } = found.plans;
      const valid = [month, quarterly, ...(monthly ? [monthly] : [])].every(p => p?.recurring && Number.isInteger(p.charge) && p.charge > 0 && p.price === p.charge && !p.remaining);
      if (!valid || ![one, three].every(v => Number.isInteger(v.price) && v.price > 0)) return false;
      // The brief changes presentation only: both 3-month plans must cost the same.
      if (monthly && Math.abs(monthly.charge * 3 - quarterly.charge) > 2) return false;
      this.variants = { 1: one, 3: three };
      this.plans = found.plans;
      return true;
    }

    t(key, values = {}) {
      let text = this.config.strings[key] || key;
      Object.entries(values).forEach(([name, value]) => { text = text.replaceAll(`{${name}}`, value); });
      return text;
    }
    price(amount) { return this.money.format(amount / 100); }
    // Paying the quarter in one go is the preselected rhythm everywhere (A step 3,
    // B1 switch, B2 second step); monthly billing stays one click away.
    defaultBilling() { return 'quarterly'; }
    plan(state = this.state) {
      if (state.mode !== 'subscription') return null;
      if (state.duration === 1) return this.plans.month;
      return (state.billing === 'monthly' && this.plans.monthly) || this.plans.quarterly;
    }
    monthlyBilled(state = this.state) { const plan = this.plan(state); return !!plan && plan === this.plans.monthly; }
    offer(state = this.state) {
      const variant = this.variants[state.duration], plan = this.plan(state);
      const charge = plan ? plan.charge : variant.price;
      const total = this.monthlyBilled(state) ? charge * 3 : charge;
      return { variant, plan, charge, total, monthly: total / state.duration };
    }
    // Subscription vs one-time purchase of the same duration, in whole percents.
    discount(duration = this.state.duration) {
      const reference = this.variants[duration].price / duration;
      const monthly = this.offer({ duration, mode: 'subscription', billing: 'monthly' }).monthly;
      return Math.max(0, Math.round((1 - monthly / reference) * 100));
    }
    saving(duration = this.state.duration) {
      return this.variants[duration].price - this.offer({ duration, mode: 'subscription', billing: 'monthly' }).total;
    }
    terms() {
      const { mode, duration, billing } = this.state;
      if (mode === 'one-time') return this.t('oneTimeTerms');
      if (duration === 1) return this.t('monthTerms');
      return billing === 'monthly' && this.plans.monthly
        ? this.t('quarterMonthlyTerms', { price: this.price(this.plans.monthly.charge) })
        : this.t('quarterTerms', { price: this.price(this.plans.quarterly.charge) });
    }
    // Before the rhythm is chosen, only the commitment is stated.
    engagement() {
      const { mode, duration } = this.state;
      if (mode === 'one-time') return this.t('oneTimeTerms');
      return duration === 1 ? this.t('monthTerms') : this.t('engagement');
    }
    label(state = this.state) {
      const offer = this.offer(state);
      if (state.mode !== 'subscription') return this.price(offer.charge);
      return state.duration === 1 || this.monthlyBilled(state)
        ? this.t('perMonth', { price: this.price(offer.charge) })
        : this.t('perQuarter', { price: this.price(offer.charge) });
    }
    // A's duration step comes before the purchase mode, so its tiles price the
    // packs themselves: one-time price, the compare-at struck in front of it.
    // In B1 and B2 the mode is already chosen when the tiles appear, so a tile
    // has to show what that offer costs — the total for the duration, struck
    // against the one-time price of the same duration. Never a monthly amount.
    tileLabel(state) {
      return this.price(this.variant === 'A' ? this.variants[state.duration].price : this.offer(state).total);
    }
    tileReference(state) {
      const variant = this.variants[state.duration];
      if (this.variant !== 'A' && state.mode === 'subscription') return variant.price;
      return variant.compareAtPrice > variant.price ? variant.compareAtPrice : 0;
    }
    fromLabel(mode) {
      return this.t('from', { price: this.t('perMonth', { price: this.price(this.offer({ duration: 3, mode, billing: 'monthly' }).monthly) }) });
    }
    billingOpen() { return this.state.mode === 'subscription' && this.state.duration === 3 && !!this.plans.monthly; }
    durationName(duration) { return this.variants[duration].title || this.t('months', { count: duration }); }

    activate(variant) {
      if (this.busy || !INTERFACES.includes(variant)) return;
      this.closeDialog();
      this.observer?.disconnect();
      this.variant = variant;
      this.host.removeAttribute('data-cro-active');
      this.hidden = true;
      this.spacingObserver?.disconnect();
      this.style.marginTop = '';
      if (variant === 'control') { this.track('view'); return; }
      this.state = { duration: 3, mode: 'subscription', billing: this.defaultBilling() };
      this.renderShell();
      this.host.dataset.croActive = variant;
      this.hidden = false;
      this.fitSpacing();
      this.track('view');
    }

    // The host lays its children out with a flex gap. Zero-height siblings
    // right above the buy box (an app block rendering nothing, such as an
    // empty Playshorts slot) still add a gap each: cancel those extra gaps, and
    // recompute when such a sibling gains content or the breakpoint changes.
    fitSpacing() {
      this.spacingObserver?.disconnect();
      const measure = () => {
        const gap = parseFloat(getComputedStyle(this.host).rowGap) || 0;
        let empty = 0;
        for (let el = this.previousElementSibling; el; el = el.previousElementSibling) {
          if (getComputedStyle(el).display === 'none') continue;
          if (el.getBoundingClientRect().height > 0) break;
          empty += 1;
        }
        this.style.marginTop = empty && gap ? `${-gap * empty}px` : '';
      };
      this.spacingObserver = new ResizeObserver(measure);
      for (let el = this.previousElementSibling; el; el = el.previousElementSibling) this.spacingObserver.observe(el);
      this.spacingObserver.observe(this.host);
      measure();
    }

    renderShell() {
      // Keep the JSON source for Shopify section reconnects.
      const source = this.querySelector('[data-buybox-config]');
      this.replaceChildren(source);
      this.insertAdjacentHTML('beforeend', `
        <div data-buybox-main></div>
        <div class="sova-buybox__sticky" data-buybox-sticky hidden><button type="button" class="sova-buybox__cta" data-action="start"></button></div>
        <dialog class="sova-buybox__dialog ${this.variant === 'B2' ? 'sova-buybox__dialog--drawer' : ''}" aria-labelledby="buybox-title-${escape(this.dataset.sectionId)}">
          <div class="sova-buybox__dialog-header"><button type="button" class="sova-buybox__text-button" data-action="back">${escape(this.t('back'))}</button><div class="sova-buybox__steps"><span data-progress></span><progress data-step-progress max="4" value="1"></progress></div><button type="button" class="sova-buybox__close" data-action="close" aria-label="${escape(this.t('close'))}">×</button></div>
          <h2 id="buybox-title-${escape(this.dataset.sectionId)}" tabindex="-1"></h2>
          <div data-dialog-content></div>
          <footer class="sova-buybox__footer"><div data-dialog-total></div><p class="sova-buybox__error" role="alert" data-dialog-error></p><button type="button" class="sova-buybox__cta" data-action="next"></button></footer>
        </dialog>`);
      this.main = this.querySelector('[data-buybox-main]');
      this.dialog = this.querySelector('dialog');
      this.dialog.addEventListener('cancel', e => { e.preventDefault(); if (!this.busy) this.abandon(); });
      this.dialog.addEventListener('click', e => { if (e.target === this.dialog && !this.busy) this.abandon(); });
      this.renderMain();
      this.observer = new IntersectionObserver(([entry]) => {
        this.querySelector('[data-buybox-sticky]').hidden = entry.isIntersecting || this.dialog.open;
      }, { threshold: 0.1 });
      this.observer.observe(this.main);
    }

    durationChoices(context) {
      return `<fieldset class="sova-buybox__choices"><legend class="sova-buybox__sr-only">${escape(this.t('chooseDuration'))}</legend>${[1, 3].map(duration => {
        const state = { ...this.state, duration }, offer = this.offer(state);
        const image = offer.variant.image;
        const reference = this.tileReference(state);
        return `<label class="sova-buybox__tile ${this.state.duration === duration ? 'is-selected' : ''}">
          <input type="radio" name="cro-duration-${escape(this.dataset.sectionId)}-${context}" value="${duration}" data-choice="duration" ${this.state.duration === duration ? 'checked' : ''} ${!offer.variant.available ? 'disabled' : ''}>
          ${duration === 3 ? `<span class="sova-buybox__badge">${escape(this.t('recommended'))}</span>` : `<span class="sova-buybox__tile-note">${escape(this.t('tryIt'))}</span>`}
          ${image ? `<img src="${escape(image)}" alt="" width="100" height="90">` : ''}
          <strong>${escape(this.durationName(duration))}</strong><span>${reference ? `<s>${escape(this.price(reference))}</s> ` : ''}${escape(this.tileLabel(state))}</span>
          ${!offer.variant.available ? `<small>${escape(this.t('unavailable'))}</small>` : ''}
        </label>`;
      }).join('')}</fieldset>`;
    }

    billingSwitch() {
      return `<fieldset class="sova-buybox__billing"><legend class="sova-buybox__billing-label">${escape(this.t('paymentTerms'))}</legend><div class="sova-buybox__segments">${['monthly', 'quarterly'].map(billing => {
        const selected = this.state.billing === billing;
        const detail = billing === 'monthly' ? this.t('timesThree', { price: this.price(this.plans.monthly.charge) }) : this.price(this.plans.quarterly.charge);
        return `<label class="sova-buybox__segment ${selected ? 'is-selected' : ''}"><input type="radio" name="cro-billing-${escape(this.dataset.sectionId)}-inline" data-choice="billing" value="${billing}" ${selected ? 'checked' : ''}><strong>${escape(this.t(billing === 'monthly' ? 'monthly' : 'payOnce'))}</strong><span>${escape(detail)}</span></label>`;
      }).join('')}</div></fieldset>`;
    }

    // B2's second drawer step: the same card pattern as the purchase modes.
    billingCards() {
      return `<fieldset class="sova-buybox__modes"><legend class="sova-buybox__sr-only">${escape(this.t('choosePaymentDrawer'))}</legend>${['monthly', 'quarterly'].map(billing => {
        const selected = this.state.billing === billing;
        const monthly = billing === 'monthly';
        const amount = monthly ? this.t('perMonth', { price: this.price(this.plans.monthly.charge) }) : this.price(this.plans.quarterly.charge);
        return `<div class="sova-buybox__card ${selected ? 'is-selected' : ''}">
          <label class="sova-buybox__card-label"><input type="radio" name="cro-billing-${escape(this.dataset.sectionId)}-drawer" data-choice="billing" value="${billing}" ${selected ? 'checked' : ''}>
          <span class="sova-buybox__card-text"><span class="sova-buybox__card-title"><strong>${escape(this.t(monthly ? 'monthly' : 'payOnce'))}</strong>${monthly ? `<span class="sova-buybox__badge">${escape(this.t('simplest'))}</span>` : ''}</span><small>${escape(this.t(monthly ? 'monthlyDetail' : 'onceDetail'))}</small></span>
          <span class="sova-buybox__card-price"><strong>${escape(amount)}</strong></span></label>
        </div>`;
      }).join('')}</fieldset>`;
    }

    modeChoices(context, withDuration = false) {
      const step = context === 'step';
      return `<fieldset class="sova-buybox__modes"><legend class="sova-buybox__sr-only">${escape(this.t('chooseMode'))}</legend>${['one-time', 'subscription'].map(mode => {
        const selected = this.state.mode === mode;
        const subscription = mode === 'subscription';
        const duration = this.state.duration;
        const state = { ...this.state, mode, billing: 'monthly' };
        let price;
        if (step && subscription) {
          // Totals for the chosen duration, against the one-time purchase of the
          // same duration. This screen never shows a monthly amount.
          const reference = this.price(this.variants[duration].price);
          price = `${this.discount() ? `<span class="sova-buybox__pill">${escape(this.t('discount', { percent: this.discount() }))}</span>` : ''}<strong>${escape(this.price(this.offer(state).total))}</strong>${this.discount() ? `<s>${escape(reference)}</s>` : ''}`;
        } else if (step) {
          price = `<strong>${escape(this.price(this.offer(state).charge))}</strong>`;
        } else {
          price = `<strong>${escape(this.fromLabel(mode))}</strong>${subscription && this.discount(3) ? `<span class="sova-buybox__pill">${escape(this.t('discount', { percent: this.discount(3) }))}</span>` : ''}`;
        }
        const detail = subscription ? this.t('durable') : '';
        return `<div class="sova-buybox__card ${selected ? 'is-selected' : ''}">
          <label class="sova-buybox__card-label"><input type="radio" name="cro-mode-${escape(this.dataset.sectionId)}-${context}" data-choice="mode" value="${mode}" ${selected ? 'checked' : ''}>
          <span class="sova-buybox__card-text"><span class="sova-buybox__card-title"><strong>${escape(this.t(subscription ? 'subscription' : 'oneTime'))}</strong>${subscription ? `<span class="sova-buybox__badge">${escape(this.t('popular'))}</span>` : ''}</span>${detail ? `<small>${escape(detail)}</small>` : ''}</span>
          <span class="sova-buybox__card-price">${price}</span></label>
          ${withDuration && selected ? `<div class="sova-buybox__card-body">${this.durationChoices('inline')}${this.billingOpen() ? this.billingSwitch() : ''}<p class="sova-buybox__terms">${escape(this.terms())}</p></div>` : ''}
        </div>`;
      }).join('')}</fieldset>`;
    }

    // The sentence is escaped first, then its placeholders are replaced by the
    // values: only what is built here carries markup, a translation never can.
    // Function replacements keep '$' in a currency from being read as a pattern.
    alternativeLine() {
      const bold = text => `<strong>${escape(text)}</strong>`;
      return escape(this.t('oneTimeAlternative'))
        .replace('{price}', () => bold(this.price(this.variants[1].price)))
        .replace('{duration}', () => escape(this.t('months', { count: 1 })))
        .replace('{note}', () => bold(this.t('oneTimeAlternativeNote')));
    }

    // Two lines only: the subscription monthly equivalent, then the one-month
    // one-time price. No struck price and no discount pill before the dialog.
    priceBlock() {
      const offer = this.offer({ duration: 3, mode: 'subscription', billing: 'monthly' });
      return `<div class="sova-buybox__price"><p class="sova-buybox__from"><span>${escape(this.t('fromLabel'))}</span><em>${escape(this.price(offer.monthly))}</em><span>${escape(this.t('perMonthSuffix'))}</span><span class="sova-buybox__from-note">${escape(this.t('fromSubscriptionNote', { duration: this.t('months', { count: 3 }) }))}</span></p>
        <p class="sova-buybox__alternative">${this.alternativeLine()}</p></div>`;
    }

    renderMain() {
      const buttonLabel = this.variant === 'B1' ? `${this.t('add')} · ${this.label()}` : this.t('add');
      this.main.innerHTML = `${this.demo ? `<p class="sova-buybox__demo">${escape(this.t('previewNote'))}</p>` : ''}${this.variant === 'A' ? this.priceBlock() : this.modeChoices('main', this.variant === 'B1')}
        ${this.variant === 'B2' ? `<p class="sova-buybox__terms">${escape(this.engagement())}</p>` : ''}
        <button type="button" class="sova-buybox__cta" data-action="start">${escape(buttonLabel)}</button>
        <p class="sova-buybox__error" role="alert" data-main-error></p><p class="sova-buybox__sr-only" role="status" data-success></p>`;
      this.querySelector('[data-buybox-sticky] button').textContent = buttonLabel;
      const unavailable = this.variant === 'B1' && !this.offer().variant.available;
      this.querySelectorAll('[data-action="start"]').forEach(button => {
        button.disabled = this.busy || unavailable;
        if (unavailable) button.textContent = this.t('unavailable');
      });
    }

    steps() {
      if (this.variant === 'B2') return this.billingOpen() ? ['duration', 'billing'] : ['duration'];
      // A opens on the duration, whose tiles show one-time prices, then the
      // purchase mode compares those same amounts with the subscription.
      return this.billingOpen() ? ['duration', 'mode', 'billing', 'summary'] : ['duration', 'mode', 'summary'];
    }
    stepName() { return this.steps()[Math.min(this.dialogStep, this.steps().length - 1)]; }
    lastStep() { return this.dialogStep >= this.steps().length - 1; }

    summary() {
      const { mode, duration, billing } = this.state;
      const rows = [
        ['rowDuration', this.durationName(duration)],
        ['rowMode', this.t(mode === 'subscription' ? 'subscription' : 'oneTime')]
      ];
      if (mode === 'subscription') rows.push(['rowBilling', this.t(duration === 1 || (billing === 'monthly' && this.plans.monthly) ? 'monthly' : 'quarterly')]);
      rows.push(['rowDelivery', this.t(mode === 'one-time' ? 'deliveryOnce' : duration === 1 ? 'deliveryMonth' : 'deliveryQuarter')]);
      return `<dl class="sova-buybox__summary">${rows.map(([key, value]) => `<div><dt>${escape(this.t(key))}</dt><dd>${escape(value)}</dd></div>`).join('')}</dl>`;
    }

    renderDialog(focusTitle = false) {
      const step = this.stepName();
      const drawer = this.variant === 'B2';
      const titles = { duration: 'chooseDuration', mode: 'chooseMode', billing: drawer ? 'choosePaymentDrawer' : 'choosePayment', summary: 'summary' };
      this.dialog.querySelector('h2').textContent = this.t(titles[step]);
      const progressText = drawer
        ? (this.steps().length > 1 && step === 'billing' ? `${this.dialogStep + 1} / ${this.steps().length}` : '')
        : this.t('steps', { current: this.dialogStep + 1, total: this.steps().length });
      this.dialog.querySelector('[data-progress]').textContent = progressText;
      const progress = this.dialog.querySelector('[data-step-progress]');
      progress.hidden = drawer;
      progress.max = this.steps().length;
      progress.value = this.dialogStep + 1;
      progress.setAttribute('aria-label', progressText || this.t(titles[step]));
      const back = this.dialog.querySelector('[data-action="back"]');
      back.hidden = this.dialogStep === 0;
      back.textContent = drawer ? `← ${this.durationName(this.state.duration)}` : this.t('back');

      let content = '';
      if (step === 'duration') {
        content = `${this.durationChoices('dialog')}<p class="sova-buybox__terms">${escape(this.engagement())}</p>${drawer ? '' : `<p class="sova-buybox__note">${escape(this.config.durationNote || this.t('ninetyDays'))}</p>`}`;
      }
      if (step === 'mode') {
        content = `${this.modeChoices('step')}${this.saving() > 0 ? `<p class="sova-buybox__saving">${escape(this.t('savingLine', { amount: this.price(this.saving()), count: this.state.duration }))}</p>` : ''}`;
      }
      if (step === 'billing' && drawer) content = `${this.billingCards()}<p class="sova-buybox__terms">${escape(this.t('bothDelivered'))}</p>`;
      if (step === 'billing' && !drawer) {
        content = `<fieldset class="sova-buybox__choices"><legend class="sova-buybox__sr-only">${escape(this.t('choosePayment'))}</legend>${['quarterly', 'monthly'].map(billing => `<label class="sova-buybox__tile ${this.state.billing === billing ? 'is-selected' : ''}"><input type="radio" name="cro-billing-${escape(this.dataset.sectionId)}" data-choice="billing" value="${billing}" ${this.state.billing === billing ? 'checked' : ''}><strong>${escape(this.t(billing))}</strong><span>${escape(billing === 'monthly' ? this.t('timesThree', { price: this.price(this.plans.monthly.charge) }) : this.price(this.plans.quarterly.charge))}</span><small>${escape(this.t(billing === 'monthly' ? 'monthlyDetail' : 'onceShort'))}</small></label>`).join('')}</fieldset><p class="sova-buybox__terms">${escape(this.t('bothDelivered'))}</p>`;
      }
      if (step === 'summary') content = `${this.summary()}<p class="sova-buybox__terms">${escape(this.terms())}</p>`;
      this.dialog.querySelector('[data-dialog-content]').innerHTML = content;

      const offer = this.offer();
      // The drawer and the first modal step only confirm a choice; totals come after.
      const showTotal = !drawer && this.dialogStep > 0;
      this.dialog.querySelector('[data-dialog-total]').innerHTML = showTotal
        ? `<strong>${escape(this.t('today', { price: this.price(offer.charge) }))}</strong>${this.state.duration === 3 && this.state.mode === 'subscription' ? `<small>${escape(this.t('cycleTotal', { price: this.price(offer.total) }))}</small>` : ''}`
        : '';
      const next = this.dialog.querySelector('[data-action="next"]');
      next.textContent = this.lastStep() ? `${this.t('add')} · ${this.label()}` : this.t('continue');
      next.disabled = !offer.variant.available || this.busy;
      if (!offer.variant.available) next.textContent = this.t('unavailable');
      if (focusTitle) this.dialog.querySelector('h2').focus();
    }

    click(event) {
      const action = event.target.closest('[data-action]')?.dataset.action;
      if (!action || this.busy) return;
      if (action === 'start') {
        if (this.variant === 'B1') { this.add(); return; }
        this.returnFocusSelector = event.target.closest('[data-buybox-sticky]') ? '[data-buybox-sticky] button' : '[data-buybox-main] [data-action="start"]';
        this.dialogStep = 0;
        this.renderDialog();
        this.dialog.showModal();
        this.track('step_view', { step: this.stepName() });
        this.previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        this.querySelector('[data-buybox-sticky]').hidden = true;
        this.dialog.querySelector('h2').focus();
      }
      if (action === 'close') this.abandon();
      if (action === 'back') { this.dialogStep = Math.max(0, this.dialogStep - 1); this.renderDialog(true); this.track('step_view', { step: this.stepName() }); }
      if (action === 'next') {
        if (this.lastStep()) this.add();
        else { this.dialogStep++; this.renderDialog(true); this.track('step_view', { step: this.stepName() }); }
      }
    }

    change(event) {
      const key = event.target.dataset.choice;
      if (!key || this.busy) return;
      const value = key === 'duration' ? Number(event.target.value) : event.target.value;
      if (!({ duration: [1, 3], mode: ['one-time', 'subscription'], billing: ['monthly', 'quarterly'] }[key] || []).includes(value)) return;
      const focusSelector = `input[data-choice="${key}"][value="${value}"]`;
      this.state[key] = value;
      this.track('selection', { field: key });
      if (this.dialog.open) { this.renderDialog(); this.dialog.querySelector(focusSelector)?.focus(); }
      else { this.renderMain(); this.main.querySelector(focusSelector)?.focus(); }
    }

    abandon() { this.track('abandon', { step: this.stepName() }); this.closeDialog(); }
    closeDialog() {
      if (!this.dialog?.open) return;
      this.dialog.close();
      document.documentElement.style.overflow = this.previousOverflow || '';
      this.renderMain();
      const rect = this.main.getBoundingClientRect();
      this.querySelector('[data-buybox-sticky]').hidden = rect.bottom > 0 && rect.top < innerHeight;
      this.querySelector(this.returnFocusSelector)?.focus({ preventScroll: true });
    }

    track(action, extra = {}) {
      const detail = { event: 'sova_buybox', action, variant: this.variant, product_id: this.config.productId, product_handle: this.config.handle, duration: this.state.duration, purchase_mode: this.state.mode, billing: this.state.mode === 'subscription' ? (this.state.duration === 1 ? 'monthly' : this.state.billing) : 'once', qa: this.qa, ...extra };
      this.dispatchEvent(new CustomEvent('sova:buybox:event', { bubbles: true, detail }));
      if (Array.isArray(window.dataLayer)) window.dataLayer.push(detail);
    }

    async add() {
      const offer = this.offer();
      if (this.busy || !offer.variant.available) return;
      if (this.demo) {
        this.querySelector(this.dialog.open ? '[data-dialog-error]' : '[data-main-error]').textContent = this.t('previewAdd');
        return;
      }
      this.busy = true;
      this.querySelectorAll('.sova-buybox__error').forEach(el => { el.textContent = ''; });
      this.querySelectorAll('button, input').forEach(el => { el.disabled = true; });
      const button = this.dialog.open ? this.dialog.querySelector('[data-action="next"]') : this.main.querySelector('[data-action="start"]');
      button.textContent = this.t('adding');
      this.track('add_attempt');
      const root = window.Shopify?.routes?.root || '/';
      let added = false;
      try {
        const item = { id: offer.variant.id, quantity: 1 };
        if (offer.plan) item.selling_plan = offer.plan.id;
        const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [item] }) });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : this.t('error'));
        added = true;
        this.track('add_success');
        this.closeDialog();
        window.Shopify?.publish?.('cart:item-added', { item: result.items?.[0] });
        const drawer = document.querySelector('apt-cart-drawer');
        if (drawer?.sectionId && typeof drawer.refreshContentWithHtml === 'function') {
          // The theme's refresh() returns void, so await the Section Rendering API
          // ourselves to avoid opening stale content or swallowing refresh failures.
          const sectionsResponse = await fetch(`${root}?sections=${encodeURIComponent(drawer.sectionId)}`);
          if (!sectionsResponse.ok) throw new Error('Cart section unavailable');
          const sections = await sectionsResponse.json();
          if (!sections[drawer.sectionId]) throw new Error('Cart section missing');
          drawer.refreshContentWithHtml(sections[drawer.sectionId], true);
          const cartResponse = await fetch(`${root}cart.js`);
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; });
          }
        } else location.assign(`${root}cart`);
      } catch (error) {
        // Once accepted by Shopify, never invite a retry that adds the item twice.
        if (added) location.assign(`${root}cart`);
        else {
          this.track('add_error');
          const target = this.dialog.open ? '[data-dialog-error]' : '[data-main-error]';
          this.querySelector(target).textContent = error.message || this.t('error');
        }
      } finally {
        this.busy = false;
        this.querySelectorAll('button, input').forEach(el => { el.disabled = false; });
        // Re-render labels/availability without destroying an error announcement.
        const error = this.querySelector('[data-main-error]').textContent;
        this.renderMain();
        this.querySelector('[data-main-error]').textContent = error;
        if (this.dialog.open) this.renderDialog();
        if (added) this.querySelector('[data-success]').textContent = this.t('added');
      }
    }
  }
  customElements.define('sova-buybox', SovaBuybox);
})();
